import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/cn";

/**
 * Button primitive
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.1
 *
 * Rules baked in:
 *   - `hover-elevate active-elevate-2` is ALWAYS applied. Never add
 *     `hover:bg-*` to a `<Button>` — the elevation overlay handles hover/press.
 *   - Heights use `min-h-*` (not fixed `h-*`) so a button auto-grows if its
 *     label wraps to two lines.
 *   - `[&_svg]:size-4 [&_svg]:shrink-0` baked in so any Lucide icon
 *     auto-sizes to 16px without per-call `h-4 w-4`.
 *   - `outline` uses the theme-aware `--button-outline` token via inline style
 *     so light/dark mode flip the rim alpha automatically.
 *   - `ghost` uses a transparent rim placeholder so toggling a colour-rim
 *     className later doesn't shift layout by 1px.
 *
 * Frequency in the canonical app (read-only stats from the legacy export):
 *   size:    sm=276  icon=121  lg=8  default=2
 *   variant: outline=486  ghost=167  secondary=90  destructive=25  default=10
 *
 * `outline` + `sm` are the workhorses; default to those for most action chrome.
 */
const buttonVariants = cva(
  [
    // base layout
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium",
    // elevation system — mandatory
    "hover-elevate active-elevate-2",
    // focus ring (1px, no offset — button already has a border/halo)
    "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    // a11y
    "disabled:pointer-events-none disabled:opacity-50",
    // icon auto-size
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground border border-primary shadow-xs",
        destructive:
          "bg-destructive text-destructive-foreground border border-destructive shadow-xs",
        outline:
          "border bg-background text-foreground shadow-xs [border-color:var(--button-outline)]",
        secondary:
          "bg-secondary text-secondary-foreground border border-secondary",
        ghost:
          // transparent rim placeholder — keeps layout stable when callers add
          // `className="border-amber-500"` etc. to give a coloured ring
          "border border-transparent text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // body buttons inside drawers/dialogs
        default: "min-h-9 px-4 py-2 text-sm",
        // List-page "New …" header, toolbar, line-row actions — dominant size
        sm: "min-h-8 px-3 text-xs",
        // Hero CTAs (login submit, public-share download)
        lg: "min-h-10 px-8 text-sm",
        // Icon-only buttons; usually paired with `variant="ghost"`
        icon: "h-9 w-9 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
